/** Keep popups (context menus, their flyouts) fully on-screen. */
export const POPUP_VIEWPORT_MARGIN = 8;
/** The gap between a button and the menu it opens. */
export const POPUP_ANCHOR_GAP = 4;

interface PopupAnchor {
  x: number;
  y: number;
}

/** A box in viewport coordinates; a `DOMRect` is one. */
export interface PopupAnchorRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Which of the button's edges the menu lines up with. */
export type PopupAlign = "start" | "end";

/**
 * Clamp a popup's top-left anchor so a popup of the given size stays
 * inside the viewport with a small margin. Popups larger than the
 * viewport pin to the margin instead of overflowing both edges.
 */
export function clampPopupToViewport(
  x: number,
  y: number,
  popupWidth: number,
  popupHeight: number,
  viewportWidth: number = typeof window === "undefined" ? 0 : window.innerWidth,
  viewportHeight: number = typeof window === "undefined"
    ? 0
    : window.innerHeight,
  margin: number = POPUP_VIEWPORT_MARGIN,
): PopupAnchor {
  const maxX = Math.max(margin, viewportWidth - popupWidth - margin);
  const maxY = Math.max(margin, viewportHeight - popupHeight - margin);
  return {
    x: Math.min(Math.max(x, margin), maxX),
    y: Math.min(Math.max(y, margin), maxY),
  };
}

/**
 * The top-left of a menu opened from a button: below it, lined up with its
 * start or end edge; above it when it does not fit below and there is more
 * room above; always clamped inside the viewport (the window's work area,
 * so never under the Dock or taskbar).
 */
export function menuAtAnchorRect(
  anchor: PopupAnchorRect,
  menuWidth: number,
  menuHeight: number,
  align: PopupAlign,
  viewportWidth: number = window.innerWidth,
  viewportHeight: number = window.innerHeight,
  margin: number = POPUP_VIEWPORT_MARGIN,
): PopupAnchor {
  const x = align === "end" ? anchor.right - menuWidth : anchor.left;
  const below = anchor.bottom + POPUP_ANCHOR_GAP;
  const roomBelow = viewportHeight - margin - below;
  const roomAbove = anchor.top - POPUP_ANCHOR_GAP - margin;
  const y =
    menuHeight <= roomBelow || roomBelow >= roomAbove
      ? below
      : anchor.top - POPUP_ANCHOR_GAP - menuHeight;
  return clampPopupToViewport(
    x,
    y,
    menuWidth,
    menuHeight,
    viewportWidth,
    viewportHeight,
    margin,
  );
}

/** Where a submenu's flyout opens beside its row. */
export interface FlyoutPlacement {
  /** The row's side the flyout opens on. */
  side: "right" | "left";
  /** Horizontal shift that keeps a flyout wider than either side on-screen. */
  offsetX: number;
  /** Vertical shift from the row's top that keeps the flyout on-screen. */
  offsetY: number;
}

export const DEFAULT_FLYOUT_PLACEMENT: FlyoutPlacement = {
  side: "right",
  offsetX: 0,
  offsetY: 0,
};

/** A flyout's measured box. */
export interface FlyoutSize {
  width: number;
  height: number;
  /** From the flyout's top to its first row's top (its padding). */
  firstRowInset: number;
}

/**
 * A flyout opens on its row's right when it fits there, otherwise on the
 * side with more room; one wider than both sides shifts back on-screen.
 * Its first row sits level with the row that opened it, shifted up or down
 * only as far as the viewport needs.
 */
export function submenuFlyoutPlacement(
  row: { left: number; right: number; top: number },
  flyout: FlyoutSize,
  viewportWidth: number = window.innerWidth,
  viewportHeight: number = window.innerHeight,
  margin: number = POPUP_VIEWPORT_MARGIN,
): FlyoutPlacement {
  const roomRight = viewportWidth - margin - row.right;
  const roomLeft = row.left - margin;
  const side =
    flyout.width <= roomRight || roomRight >= roomLeft ? "right" : "left";
  const x = side === "right" ? row.right : row.left - flyout.width;
  const y = row.top - flyout.firstRowInset;
  const clamped = clampPopupToViewport(
    x,
    y,
    flyout.width,
    flyout.height,
    viewportWidth,
    viewportHeight,
    margin,
  );
  return { side, offsetX: clamped.x - x, offsetY: clamped.y - row.top };
}
